/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Empty, Modal, Row, Space, Typography } from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
import EchartsWrapper from './EchartsWrapper';
import ChartLoader from './ChartLoader';

// Misc imports
import is from '../../utils/is';
import { CSAT_ANALYTICS_THRESHOLDS } from '../../constants/main';
import useTheme from '../../utils/hooks/useTheme';
import { formatNumberToThousandSeparator } from '../../utils/string/formatNumberToThousandSeparator';

const CSATOverallScoreSection = ({ data = {}, sectionTitle = '', seriesName = '', isLoading = true, renderOnlyChart = false, renderOnlyTable = false }) => {
  const [chartToFullscreen, setChartToFullscreen] = useState();
  const { t } = useTranslation();
  const { successColorAnalytics: successColor, warningColorAnalytics: warningColor, errorColorAnalytics: errorColor, textColor } = useTheme();

  const getOptions = (chartData) => {
    const percentage = chartData?.averageScorePCT || 0;

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          return `${params.name}`;
        }
      },
      series: [
        {
          type: 'gauge',
          startAngle: 180,
          endAngle: 0,
          radius: '100%',
          pointer: {
            show: true,
            length: '60%',
            width: 4,
            itemStyle: {
              color: textColor
            }
          },
          anchor: {
            show: true,
            size: 14,
            itemStyle: {
              color: textColor
            }
          },
          progress: {
            show: false
          },
          axisLine: {
            lineStyle: {
              width: 0
            }
          },
          axisTick: {
            show: false
          },
          splitLine: {
            show: false
          },
          axisLabel: {
            show: false
          },
          detail: {
            show: true,
            valueAnimation: true,
            offsetCenter: [0, '30%'],
            fontSize: 36,
            fontWeight: 'bold',
            formatter: '{value}%',
            color: textColor
          },
          data: [
            {
              value: percentage
            }
          ]
        },
        {
          type: 'pie',
          radius: ['70%', '95%'],
          startAngle: 180,
          endAngle: 0,
          label: {
            show: false
          },
          labelLine: {
            show: false
          },
          data: [
            { value: 49.99, name: CSAT_ANALYTICS_THRESHOLDS.NEGATIVE.label, itemStyle: { color: errorColor } },
            { value: 25.1, name: CSAT_ANALYTICS_THRESHOLDS.NEUTRAL.label, itemStyle: { color: warningColor } },
            { value: 24.99, name: CSAT_ANALYTICS_THRESHOLDS.POSITIVE.label, itemStyle: { color: successColor } }
          ]
        }
      ]
    };
  };

  const chart = (
    <Fragment>
      {data.averageScorePCT !== 0 ? (
        <Fragment>
          <Row>
            <Col xs={24} className="d-flex justify-content-center align-items-center mt-12">
              <EchartsWrapper externalClassname="d-flex justify-content-center" chartOptions={getOptions(data)} style={{ height: 367, minHeight: '367px' }} loading={isLoading} />
            </Col>
            <Col xs={24} className="d-flex justify-content-center align-items-start position-relative">
              <Space direction="vertical" className="position-absolute" style={{ bottom: '20px' }}>
                <Typography.Text className="text-muted fs-xl opacity-70" strong>
                  {formatNumberToThousandSeparator(data?.count)} {t('Responses')}
                </Typography.Text>
                <Typography.Text className="text-muted fs-xl opacity-70" strong>
                  {data?.averageScore}/5 {t('Rating Score')}
                </Typography.Text>
              </Space>
            </Col>
          </Row>
        </Fragment>
      ) : (
        <div className="d-flex content-fill flex-column h-100">
          <div className="mt-5 d-flex align-items-center mb-auto text-center flex-auto align-self-center">
            <Empty
              description={
                <Typography.Paragraph>
                  {t('No data available for this chart')}
                </Typography.Paragraph>
              }
            />
          </div>
        </div>
      )}
    </Fragment>
  );

  return (
    <Fragment>
      {renderOnlyChart ? (
        chart
      ) : (
        <Fragment>
          <div className="mb-6 position-relative">
            <Typography.Title level={4} className="mb-6">
              <Typography.Text className="mr-2">{sectionTitle}</Typography.Text>
            </Typography.Title>
            {isLoading ? (
              <ChartLoader />
            ) : (
              <Fragment>
                {chart}
              </Fragment>
            )}

            <Button
              type="text"
              size="large"
              disabled={isLoading}
              icon={<FullscreenOutlined />}
              className="position-absolute top-0 right-0"
              onClick={() => setChartToFullscreen(chart)}
            />
          </div>

          <Modal
            open={is.truthy(chartToFullscreen)}
            onCancel={() => setChartToFullscreen()}
            destroyOnClose
            footer={null}
            centered
            style={{ maxWidth: 1000 }}
            width={'95%'}
          >
            <div className="p-5">{chartToFullscreen}</div>
          </Modal>
        </Fragment>
      )}
    </Fragment>
  );
};

CSATOverallScoreSection.propTypes = {
  data: PropTypes.object,
  sectionTitle: PropTypes.string,
  seriesName: PropTypes.string,
  isLoading: PropTypes.bool,
  renderOnlyChart: PropTypes.bool,
  renderOnlyTable: PropTypes.bool
};

export default CSATOverallScoreSection;
